import React from "react";
import { getDescParentChildrenCounts } from "../../../Utils/dashboardDescParentChildren";
import { getDescByStateCounts } from "../../../Utils/dashboardDescByState";
import { getDescAgeGroupCounts } from "../../../Utils/dashboardDescAgeGroup";

/**
 * Catalog of the dashboard's stat cards ("controls") — the pool the Customize
 * Controls editor adds from, removes to and reorders. One entry per detail page.
 *
 * Adding a page: append an entry here and it becomes customizable everywhere.
 * A new entry lands in Quick Actions for anyone who already saved a layout, so
 * nobody's dashboard rearranges itself under them. `defaultVisible: false`
 * keeps it out of the grid for first-time users too.
 *
 * The grid holds exactly this many cards — no more, no fewer. Customizing is a
 * swap, not a free-for-all, so the top row always reads as a full block.
 */
export const DASHBOARD_VISIBLE_CONTROL_COUNT = 8;

export const DASHBOARD_CONTROLS = [
  {
    id: "heads_of_line",
    label: "Heads of Line/Color",
    icon: "/assets/images/loop-lines.svg",
    route: "/dashboard/heads-of-line",
    getValue: ({ statistics }) => statistics?.family_lines_count ?? 0,
  },
  {
    id: "total_family_members",
    label: "Family Members",
    icon: "/assets/images/members.svg",
    route: "/dashboard/total-family-members",
    getValue: ({ statistics }) => statistics?.total_family_members ?? 0,
  },
  {
    id: "total_male_female",
    label: "Males/Females",
    icon: "/assets/images/male-female.svg",
    route: "/dashboard/total-male-female",
    getValue: ({ statistics }) =>
      statistics?.total_male || statistics?.total_female
        ? `${statistics?.total_male ?? 0}/${statistics?.total_female ?? 0}`
        : "0/0",
  },
  {
    id: "birthdays_this_month",
    label: "Birthdays this Month",
    icon: "/assets/images/birthday.svg",
    route: "/dashboard/birthdays-this-month",
    getValue: ({ statistics }) => statistics?.birthdays_this_month ?? 0,
  },
  {
    id: "anniversaries_this_month",
    label: "Wedding Anniv for the Year",
    icon: "/assets/images/Anniversary.svg",
    route: "/dashboard/anniversaries-this-month",
    // The whole-year recount (same rows as the detail page). The raw statistic
    // is only this month's anniversaries, so it is not shown as a stand-in —
    // the card stays blank (a no-break space keeps the line's height) until the
    // recount lands rather than flashing it.
    getValue: ({ anniversariesThisMonthCount }) =>
      anniversariesThisMonthCount ?? "\u00A0",
  },
  {
    id: "milestone_wedding_anniversaries",
    label: "Milestone Wedding Anniv",
    icon: "/assets/images/Anniversary.svg",
    route: "/dashboard/milestone-wedding-anniversaries",
    // Starts in Quick Actions rather than the grid, which is already full at 8.
    defaultVisible: false,
    // Off the report's own endpoint — the same call the detail page makes, so
    // the card and the page carry one total.
    getValue: ({ milestoneAnniversariesCount }) => milestoneAnniversariesCount ?? 0,
  },
  {
    id: "memorials_this_month",
    label: "Memoriams this Month",
    icon: "/assets/images/remembered.svg",
    route: "/dashboard/memorials-this-month",
    getValue: ({ statistics }) => statistics?.memorials_this_month ?? 0,
  },
  {
    id: "ages_milestone",
    label: "Milestone Ages this Year",
    icon: "/assets/images/ageMilestone.svg",
    route: "/dashboard/ages-milestone",
    // The statistics total counts the in-laws the report leaves out, so the
    // card counts the detail rows the page lists instead.
    getValue: ({ ageMilestonesCount, statistics }) =>
      ageMilestonesCount ?? statistics?.age_milestones ?? 0,
  },
  // No Monthly Milestone Ages card: Milestone Ages this Year now carries the
  // month picker itself, so the month a reader wants is a filter on that one
  // report rather than a second card beside it. A layout saved while the card
  // existed still loads — splitDashboardControls drops an id the catalog no
  // longer knows and normalizeDashboardControlLayout backfills the slot.
  {
    id: "age_by_group",
    label: "Decade Ages this Year",
    icon: "/assets/images/decadeAges.svg",
    route: "/dashboard/age-by-group",
    getValue: ({ statistics }) => statistics?.age_group ?? 0,
  },
  {
    id: "multi_births_by_generation",
    label: "Multi-Births by Generation",
    icon: "/assets/images/multiBirths.svg",
    route: "/dashboard/multi-births-by-generation",
    // Starts in Quick Actions rather than the grid, which is already full at 8.
    defaultVisible: false,
    // The count comes from the dashboard statistics block, same as every other
    // card. The derived count off the statistics-with-details member rows is
    // only a fallback, for a payload that predates the total.
    getValue: ({ statistics, multiBirthsCount }) =>
      statistics?.multiple_births_count ??
      statistics?.multi_births_count ??
      statistics?.multi_births_by_generation ??
      statistics?.multiple_births_by_generation ??
      statistics?.multi_births ??
      statistics?.multiple_births ??
      multiBirthsCount ??
      0,
  },
  {
    id: "desc_in_age_generation",
    label: "Generations & Ages (All Related)",
    // The card prints the label on two lines so "(All Related)" never splits
    // across a wrap; the plain label above still names the card everywhere else.
    labelLines: ["Generations & Ages", "(All Related)"],
    icon: "/assets/images/descAgeGeneration.svg",
    route: "/dashboard/desc-in-age-generation",
    // Starts in Quick Actions rather than the grid, which is already full at 8.
    defaultVisible: false,
    // Straight off /report-management/family-in-age-order — the report's own
    // endpoint and its own count of the living descendants, which is also the
    // number the detail page prints in its heading.
    getValue: ({ descendantsCount }) => descendantsCount ?? 0,
  },
  {
    id: "desc_parent_children",
    label: "Parent(s) and Child(ren)",
    icon: "/assets/images/descParentChildren.svg",
    route: "/dashboard/desc-parent-children",
    // Starts in Quick Actions rather than the grid, which is already full at 8.
    defaultVisible: false,
    // Off the dashboard statistics block, like most cards: descendant parents
    // over descendant parents and children together, each number named, on
    // one line — "72 (Family Units)/184 (Individuals)". Wider than the space
    // beside the icon, so the card lays it under the label instead, at the
    // same size as every other card's total, breaking after the "/" where
    // the card is too narrow (see App.css). The detail page prints the same figure
    // plain, "72/184", in its heading.
    getValue: ({ statistics }) => {
      const { familyUnits, individuals } = getDescParentChildrenCounts(statistics);
      return (
        <>
          <span className="dashboard-control-count-part">
            {familyUnits}
            <span className="dashboard-control-count-label">(Family Units)</span>/
          </span>
          <wbr />
          <span className="dashboard-control-count-part">
            {individuals}
            <span className="dashboard-control-count-label">(Individuals)</span>
          </span>
        </>
      );
    },
  },
  {
    id: "desc_by_state",
    label: "Family by State",
    icon: "/assets/images/descByState.svg",
    route: "/dashboard/desc-by-state",
    // Starts in Quick Actions rather than the grid, which is already full at 8.
    defaultVisible: false,
    // Off the dashboard statistics block, like most cards: states of birth
    // over the family members born in them, each number named, as the detail
    // page's heading prints them — "13 States/155 FMs". Laid under the label
    // like the Desc Parent card's figure (see App.css).
    getValue: ({ statistics }) => {
      const { states, members } = getDescByStateCounts(statistics);
      return (
        <>
          <span className="dashboard-control-count-part">
            {states}
            <span className="dashboard-control-count-label">
              {states === 1 ? "State" : "States"}
            </span>/
          </span>
          <wbr />
          <span className="dashboard-control-count-part">
            {members}
            <span className="dashboard-control-count-label">
              {members === 1 ? "FM" : "FMs"}
            </span>
          </span>
        </>
      );
    },
  },
  {
    id: "family_members_since_year",
    label: "Family Members (Since 2000)",
    icon: "/assets/images/newFamilyMembers.svg",
    route: "/dashboard/family-members-since-2000",
    // Starts in Quick Actions rather than the grid, which is already full at 8.
    defaultVisible: false,
    // Off /report-management/family-members-by-year-of-birth — the report's own
    // endpoint and its own total, which is also the number the detail page
    // prints in its heading, so the card and the page carry one total.
    getValue: ({ familyMembersSinceYearCount }) => familyMembersSinceYearCount ?? 0,
  },
  {
    id: "family_heads",
    label: "Family Head Grand Count",
    icon: "/assets/images/familyHeads.svg",
    route: "/dashboard/family-heads",
    // Starts in Quick Actions rather than the grid, which is already full at 8.
    defaultVisible: false,
    // Off /report-management/family-heads-grand-count — the same call the
    // detail page makes, so the card and the page carry one grand total.
    getValue: ({ familyHeadsGrandTotal }) => familyHeadsGrandTotal ?? 0,
  },
  {
    id: "birthdays_by_family_lines",
    label: "Birthdays by Family Lines",
    icon: "/assets/images/birthday.svg",
    route: "/dashboard/birthdays-by-family-lines",
    // Starts in Quick Actions rather than the grid, which is already full at 8.
    defaultVisible: false,
    // Off /report-management/birthdays-by-family-lines-report — the same call
    // the detail page makes, so the card and the page carry one total.
    getValue: ({ birthdaysByFamilyLinesCount }) => birthdaysByFamilyLinesCount ?? 0,
  },
  {
    id: "birth_by_day",
    label: "Birth Day (1-31, Calendar)",
    icon: "/assets/images/birthday.svg",
    route: "/dashboard/birth-by-day",
    // Starts in Quick Actions rather than the grid, which is already full at 8.
    defaultVisible: false,
    // Off /report-management/birth-by-day — the same call the detail page
    // makes, so the card and the page carry one total.
    getValue: ({ birthByDayCount }) => birthByDayCount ?? 0,
  },
  {
    id: "descendant_age_group",
    label: "Age Groups (Related)",
    icon: "/assets/images/descAgeGroup.svg",
    route: "/dashboard/descendant-age-group",
    // Starts in Quick Actions rather than the grid, which is already full at 8.
    defaultVisible: false,
    // The two brackets the detail page's columns are titled with, each number
    // named, on one line like the Desc Parent card — "49 (17 & Younger)/106
    // (18 & Older)" — and laid under the label the same way (see App.css).
    // Off the dashboard statistics block's descendants_aged_1_to_17_count and
    // descendants_aged_18_and_above_count, else off the report's own endpoint
    // — the same call the detail page makes, so the card and the page carry
    // one pair of numbers.
    getValue: ({ statistics, descAgeGroupReport }) => {
      const counts = getDescAgeGroupCounts(statistics, descAgeGroupReport);
      return (
        <>
          <span className="dashboard-control-count-part">
            {counts?.minors ?? 0}
            <span className="dashboard-control-count-label">(17 &amp; Younger)</span>/
          </span>
          <wbr />
          <span className="dashboard-control-count-part">
            {counts?.adults ?? 0}
            <span className="dashboard-control-count-label">(18 &amp; Older)</span>
          </span>
        </>
      );
    },
  },
];

const CONTROLS_BY_ID = DASHBOARD_CONTROLS.reduce((map, control) => {
  map[control.id] = control;
  return map;
}, {});

export function getDashboardControl(id) {
  return CONTROLS_BY_ID[id] || null;
}

/** Layout a user sees before they have ever customized anything. */
export const DEFAULT_VISIBLE_CONTROL_IDS = DASHBOARD_CONTROLS.filter(
  (control) => control.defaultVisible !== false
).map((control) => control.id);

/**
 * Split an id list into the two lists the dashboard renders: the cards on the
 * grid and the leftovers that show up under Quick Actions.
 *
 * Takes the list as given, count and all — a draft mid-edit is legitimately
 * short by one while the user picks a replacement.
 */
export function splitDashboardControls(ids) {
  const seen = new Set();
  const visibleIds = [];
  (ids || []).forEach((id) => {
    if (!CONTROLS_BY_ID[id] || seen.has(id)) return;
    seen.add(id);
    visibleIds.push(id);
  });

  const hiddenIds = DASHBOARD_CONTROLS.filter(
    (control) => !seen.has(control.id)
  ).map((control) => control.id);

  return { visibleIds, hiddenIds };
}

/**
 * A stored layout, forced back to exactly DASHBOARD_VISIBLE_CONTROL_COUNT
 * cards. Applied on load, never to a draft: a short list is topped up from the
 * catalog and a long one trimmed, so a stale or hand-edited payload can't leave
 * the grid with a ragged row.
 *
 * `savedVisibleIds` of null/undefined means "never customized" → defaults.
 */
export function normalizeDashboardControlLayout(savedVisibleIds) {
  const source = Array.isArray(savedVisibleIds)
    ? savedVisibleIds
    : DEFAULT_VISIBLE_CONTROL_IDS;

  const { visibleIds, hiddenIds } = splitDashboardControls(source);
  const filled = visibleIds.slice(0, DASHBOARD_VISIBLE_CONTROL_COUNT);

  let index = 0;
  while (filled.length < DASHBOARD_VISIBLE_CONTROL_COUNT && index < hiddenIds.length) {
    filled.push(hiddenIds[index]);
    index += 1;
  }

  return splitDashboardControls(filled);
}

/** Ids → catalog entries, dropping anything the catalog no longer knows. */
export function toDashboardControls(ids) {
  return (ids || []).map((id) => CONTROLS_BY_ID[id]).filter(Boolean);
}

/** Same ids in the same order? Used to skip no-op saves. */
export function isSameControlLayout(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  return a.length === b.length && a.every((id, index) => id === b[index]);
}
